import { apiFetch } from './v2Client';

export interface EctdV4DraftContact {
  typeCode: string;
  givenName: string;
  familyName: string;
  email: string;
  phone: string;
}
export interface EctdV4JapanMetadata {
  receptionNumber: string;
  applicationDate: string;
  brandName: string;
  applicantName: string;
  ingredients: Array<{ name: string; nameTypeCode: string }>;
  productCategoryCodes: string[];
  hasRelatedApplications: boolean | null;
  applicationReference?: string;
}
export interface EctdV4DraftMetadata {
  profileId: string;
  japan?: EctdV4JapanMetadata;
  title?: string;
  contacts: EctdV4DraftContact[];
  documents: Array<{ sourceDocumentId: string; contextOfUseCode: string; title: string }>;
}
export interface EctdV4CodeOption { code: string; label: string; codeSystem: string }
export interface JapanValidationRuleResult {
  ruleId: string;
  status: 'passed' | 'failed' | 'not-run' | 'not-applicable';
  message: string;
  evidence: string[];
  source: { url: string; section?: string; page?: number };
}
export interface JapanPackageValidationReport {
  reportVersion: '1';
  profileId: string;
  scopeLabel: string;
  packageSha256: string;
  results: JapanValidationRuleResult[];
  summary: { passed: number; failed: number; notRun: number; notApplicable: number };
  scopedChecksPassed: boolean;
  finalReady: false;
  finalBlockers: string[];
}
export interface EctdV4DraftArtifact {
  id: string;
  createdAt: string;
  sha256: string;
  revision: number;
  status: 'draft-unvalidated';
  verification?: { schema: 'passed'; integrity: 'passed'; comprehensiveValidation: 'not-run'; japanValidation?: Omit<JapanPackageValidationReport, 'results'> };
}
export interface EctdV4DraftWorkspace {
  submission: { id: string; name: string | null; region: string; ectdVersion: string; sequenceNumber: string | null; applicationNumber: string | null };
  eligibility: { supported: boolean; editable: boolean; reasons: string[] };
  outputSequenceNumber?: number | null;
  sequenceNotice?: string;
  catalog: {
    profiles: Array<{ id: string; region?: string; label: string; scope: string; ichIgVersion: string; regionalIgVersion: string; sources: Array<{ url: string; description: string }> }>;
    contextOfUseOptions: EctdV4CodeOption[];
    contactTypeOptions: EctdV4CodeOption[];
    japan?: { substanceNameTypeOptions: EctdV4CodeOption[]; productCategoryOptions: EctdV4CodeOption[]; applicationDateMinimum: string };
  };
  draft: { revision: number; metadata: EctdV4DraftMetadata } | null;
  documents: Array<{ id: string; fileName: string | null; title: string | null; revision: string | number | null; outputFileName?: string }>;
  readiness: { ready: boolean; errors: Array<{ field: string; code: string; message: string }>; warnings: string[] };
  artifacts: EctdV4DraftArtifact[];
}

export const ectdV4DraftApi = {
  validationReport: (id: string, artifactId: string): Promise<JapanPackageValidationReport> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft/artifacts/${encodeURIComponent(artifactId)}/validation-report`),
  revalidate: (id: string, artifactId: string): Promise<{ report: JapanPackageValidationReport; assessment: 'current'; artifactId: string; checkedAt: string }> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft/artifacts/${encodeURIComponent(artifactId)}/revalidate`, { method: 'POST', json: {} }),
  get: (id: string): Promise<EctdV4DraftWorkspace> => apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft`),
  save: (id: string, body: { expectedRevision: number | null; metadata: EctdV4DraftMetadata }): Promise<EctdV4DraftWorkspace> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft`, { method: 'PUT', json: body }),
  generate: (id: string, expectedRevision: number): Promise<EctdV4DraftArtifact> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft/generate`, { method: 'POST', json: { expectedRevision } }),
  download: (id: string, artifactId: string): Promise<Response> =>
    apiFetch(`/api/v1/submissions/${encodeURIComponent(id)}/ectd-v4-draft/artifacts/${encodeURIComponent(artifactId)}/download`, { raw: true }),
};
